import { createFileRoute, Outlet, redirect, Link, useLocation, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useAuthStore } from '~/client/common/stores/auth'
import { isRevelstreetStaffUser } from '~/shared/user/user.staff'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarInset,
  SidebarTrigger,
  SidebarSeparator,
} from '~/client/components/ui/sidebar'
import {
  Search,
  LayoutTemplate,
  Shield,
  ArrowLeft,
  UserCog,
  Home,
  Database,
  Bug,
  Sparkles,
} from 'lucide-react'
import { Button } from '~/client/components/ui/button'
import Avvvatars from 'avvvatars-react'

export const Route = createFileRoute('/admin')({
  beforeLoad: async () => {
    if (typeof window === 'undefined') return

    await useAuthStore.getState().initializeAuth()
    const { user } = useAuthStore.getState()

    if (!user) {
      throw redirect({ to: '/' })
    }

    // Always refresh from DB to get the authoritative is_superuser /
    // active_entity values, never rely solely on a potentially stale
    // localStorage cache.
    await useAuthStore.getState().refreshUserDetail()
    const { userDetail } = useAuthStore.getState()

    // Layout is reachable by superusers and Revelstreet staff (entity 47).
    // Per-tool routes apply their own stricter guards where needed.
    if (!isRevelstreetStaffUser(userDetail)) {
      throw redirect({ to: '/' })
    }
  },
  component: AdminLayout,
})

type NavItem = {
  label: string
  href: string
  icon: typeof Home
  exact?: boolean
  description?: string
  superuserOnly?: boolean
}

type NavGroup = {
  group: string
  items: readonly NavItem[]
}

const NAV_ITEMS: readonly NavGroup[] = [
  {
    group: 'Overview',
    items: [
      {
        label: 'Dashboard',
        href: '/admin',
        icon: Home,
        exact: true,
        superuserOnly: true,
      },
    ],
  },
  {
    group: 'Tools',
    items: [
      {
        label: 'Typesense Comparison',
        href: '/admin/typesense',
        icon: Search,
        description: 'Compare Supabase vs Typesense results',
        superuserOnly: true,
      },
      {
        label: 'Template Editor',
        href: '/admin/template-editor',
        icon: LayoutTemplate,
        description: 'Edit presentation templates',
        superuserOnly: true,
      },
      {
        label: 'Revel Recs',
        href: '/admin/recs',
        icon: Sparkles,
        description: 'Manage event recommendations',
      },
    ],
  },
  {
    group: 'User Management',
    items: [
      {
        label: 'Impersonate User',
        href: '/admin/impersonate',
        icon: UserCog,
        description: 'Sign in as another user',
        superuserOnly: true,
      },
    ],
  },
]

function AdminSidebar() {
  const location = useLocation()
  const userDetail = useAuthStore((s) => s.userDetail)
  const isSuperuser = !!userDetail?.is_superuser

  const displayName = userDetail
    ? `${[userDetail.first_name, userDetail.last_name].filter(Boolean).join(' ')}`.trim() ||
      userDetail.email ||
      'Admin'
    : 'Admin'
  const avatarValue = userDetail
    ? (userDetail.first_name?.[0] || '') + (userDetail.last_name?.[0] || '') ||
      userDetail.email ||
      ''
    : ''

  const isActive = (href: string, exact?: boolean) => {
    if (exact) {
      return location.pathname === href
    }
    return location.pathname.startsWith(href)
  }

  const visibleGroups = NAV_ITEMS
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => isSuperuser || !item.superuserOnly),
    }))
    .filter((group) => group.items.length > 0)

  return (
    <Sidebar variant="inset" collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/admin">
                <div className="flex items-center justify-center rounded-lg bg-gradient-to-br from-violet-600 to-purple-700 text-white size-8">
                  <Shield className="size-4" />
                </div>
                <div className="flex flex-col gap-0.5 leading-none">
                  <span className="font-semibold text-sm">Admin Panel</span>
                  <span className="text-xs text-muted-foreground">RevelStreet</span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarSeparator />

      <SidebarContent>
        {visibleGroups.map((group) => (
          <SidebarGroup key={group.group}>
            <SidebarGroupLabel>{group.group}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(item.href, item.exact)}
                      tooltip={item.label}
                    >
                      <Link to={item.href}>
                        <item.icon className="size-4" />
                        <span>{item.label}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <SidebarSeparator />
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="Back to App">
              <Link to="/">
                <ArrowLeft className="size-4" />
                <span>Back to App</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" className="pointer-events-none">
              <div className="flex items-center justify-center rounded-full overflow-hidden size-8">
                <Avvvatars size={32} style="character" value={avatarValue} />
              </div>
              <div className="flex flex-col gap-0.5 leading-none">
                <span className="text-xs font-medium truncate">{displayName}</span>
                <span className="text-[10px] text-muted-foreground truncate">
                  {userDetail?.email || ''}
                </span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}

function AdminLayout() {
  const userDetail = useAuthStore((s) => s.userDetail)
  const initialized = useAuthStore((s) => s.initialized)
  const navigate = useNavigate()
  const allowed = isRevelstreetStaffUser(userDetail)

  // Component-level guard: if the store updates and the user no longer
  // qualifies as Revelstreet staff (e.g. SSR hydration, auth state change, or
  // session expiry), redirect immediately and render nothing.
  useEffect(() => {
    if (initialized && !allowed) {
      navigate({ to: '/' })
    }
  }, [initialized, allowed, navigate])

  if (!initialized || !allowed) {
    return null
  }

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full overflow-hidden">
        <AdminSidebar />
        <SidebarInset>
          <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
            <SidebarTrigger className="-ml-1" />
            <div className="h-4 w-px bg-border" />
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-amber-800">
                <Shield className="size-3" />
                Admin
              </span>
            </div>
          </header>
          <main className="flex-1 overflow-auto">
            <Outlet />
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  )
}
